import type {
  ClientRequestAmbiguousField,
  ClientRequestUnmappedFilter,
  ClientRequestUnmappedFilterCode,
} from '../Ads/utils/clientRequestPrefill';

import styles from './StorefrontPrefillNotice.module.scss';

interface StorefrontPrefillNoticeProps {
  ambiguousFields: ClientRequestAmbiguousField[];
  unmappedFilters: ClientRequestUnmappedFilter[];
}

const ambiguousFieldLabels: Record<ClientRequestAmbiguousField['field'], string> = {
  baths: 'Bathrooms',
  completionStatus: 'Completion status',
  location: 'Location',
  propertyKind: 'Property type',
};

const unmappedFilterLabels: Record<ClientRequestUnmappedFilterCode, string> = {
  creator: 'Listed by',
  downPayment: 'Down payment',
  floor: 'Floor level',
  floorsNumFrom: 'Minimum floor number',
  floorsNumTo: 'Maximum floor number',
  furniture: 'Furniture',
  handover: 'Handover',
  isMyAgency: 'My agency listings',
  keywords: 'Keywords',
  multiHandover: 'Handover years',
  myFriends: 'Friends’ listings',
  occupancy: 'Occupancy',
  postHandover: 'Post-handover payment plan',
  renovation: 'Renovation',
  transactionType: 'Transaction type',
};

const humanizeValue = (value: string): string => {
  const normalizedValue = value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .trim();

  return normalizedValue
    ? `${normalizedValue.charAt(0).toUpperCase()}${normalizedValue.slice(1)}`
    : normalizedValue;
};

const formatAmbiguousValue = (item: ClientRequestAmbiguousField): string => {
  if (item.field === 'location') {
    return item.reason === 'multiple-values-for-scalar'
      ? 'Multiple locations were selected; check or refine the imported location'
      : 'Location text was imported; check or refine the location manually';
  }

  return item.sourceValue.map((value) => humanizeValue(value)).join(', ');
};

const formatUnmappedValue = (item: ClientRequestUnmappedFilter): string => {
  const { value } = item;

  if (Array.isArray(value)) return value.map((entry) => humanizeValue(String(entry))).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return humanizeValue(value);

  if (item.filter === 'creator') {
    const name = 'name' in value && typeof value.name === 'string' ? value.name.trim() : '';
    const kind = 'kind' in value && typeof value.kind === 'string' ? value.kind : '';

    return name || (kind ? humanizeValue(kind) : 'Selected creator');
  }

  if (item.filter === 'handover') {
    const quarter =
      'quarter' in value && typeof value.quarter === 'string' ? value.quarter : undefined;
    const year = 'year' in value && typeof value.year === 'string' ? value.year : undefined;

    return [quarter, year].filter(Boolean).join(' ');
  }

  return 'Selected';
};

export const StorefrontPrefillNotice = ({
  ambiguousFields,
  unmappedFilters,
}: StorefrontPrefillNoticeProps) => {
  if (!ambiguousFields.length && !unmappedFilters.length) return null;

  return (
    <section
      aria-labelledby='storefront-prefill-notice-title'
      className={styles.notice}
      role='status'
    >
      <h2 className={styles.title} id='storefront-prefill-notice-title'>
        Review imported search criteria
      </h2>
      <p className={styles.description}>
        Some criteria need to be checked or completed manually before publishing.
      </p>
      {ambiguousFields.length > 0 && (
        <div className={styles.group}>
          <h3 className={styles.groupTitle}>Choose manually</h3>
          <ul className={styles.list}>
            {ambiguousFields.map((item) => (
              <li key={`${item.field}-${item.reason}`}>
                <strong>{ambiguousFieldLabels[item.field]}:</strong> {formatAmbiguousValue(item)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {unmappedFilters.length > 0 && (
        <div className={styles.group}>
          <h3 className={styles.groupTitle}>Not transferred automatically</h3>
          <ul className={styles.list}>
            {unmappedFilters.map((item) => (
              <li key={item.filter}>
                <strong>{unmappedFilterLabels[item.filter]}:</strong> {formatUnmappedValue(item)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
